import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight, Check, LayoutDashboard, Circle } from "lucide-react";
import { SectionHeading } from "./Section";
import { TiltCard, Reveal } from "../../lib/ui-motion";
import { useLang } from "../../i18n";

const accentDots = ["bg-flame", "bg-emerald-400", "bg-sky-400"];

const MockUI = ({ project }) => (
  <div className="relative h-full w-full overflow-hidden rounded-xl border border-white/10 bg-[#0a0a0c]">
    {/* top bar */}
    <div className="flex items-center gap-2 border-b border-white/10 px-4 py-2.5">
      <div className="flex gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
        <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
        <span className="h-2.5 w-2.5 rounded-full bg-flame/60" />
      </div>
      <span className="ml-2 font-mono text-[10px] text-white/40">{project.name}</span>
    </div>
    <div className="grid grid-cols-12 h-[calc(100%-41px)]">
      {/* sidebar */}
      <div className="col-span-3 border-r border-white/10 p-3 space-y-2">
        {project.features.slice(0, 5).map((f, i) => (
          <div key={i} className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-[9px] ${i === 0 ? "bg-flame/15 text-flame" : "text-white/40"}`}>
            <Circle size={6} className="shrink-0" /> <span className="truncate">{f}</span>
          </div>
        ))}
      </div>
      {/* main */}
      <div className="col-span-9 p-3 space-y-3">
        <div className="grid grid-cols-3 gap-2">
          {project.impact.map((m, i) => (
            <div key={i} className="rounded-md border border-white/10 bg-white/[0.03] p-2">
              <div className="text-[8px] text-white/40 truncate">{m.l}</div>
              <div className="font-heading text-sm font-bold text-flame">{m.v}</div>
            </div>
          ))}
        </div>
        <div className="rounded-md border border-white/10 bg-white/[0.02] p-3">
          <div className="flex items-end gap-1.5 h-16">
            {[0.4, 0.6, 0.45, 0.75, 0.6, 0.85, 0.7, 0.95, 0.8, 1].map((v, i) => (
              <motion.div key={i} className="flex-1 rounded-sm bg-gradient-to-t from-flame/30 to-flame/80"
                initial={{ height: 0 }} animate={{ height: `${v * 100}%` }} transition={{ delay: 0.2 + i * 0.04, duration: 0.5 }} />
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          {[0, 1, 2].map((r) => (
            <div key={r} className="flex items-center justify-between rounded-md border border-white/5 bg-white/[0.02] px-2.5 py-1.5">
              <div className="flex items-center gap-2">
                <span className={`h-1.5 w-1.5 rounded-full ${accentDots[r]}`} />
                <span className="h-1.5 w-16 rounded-full bg-white/15" />
              </div>
              <span className="h-1.5 w-10 rounded-full bg-white/10" />
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export const FeaturedProjects = () => {
  const { t } = useLang();
  const p = t.projects;
  const [open, setOpen] = useState(null);

  useEffect(() => {
    document.body.style.overflow = open !== null ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <section id="projects" className="relative py-24 md:py-36" data-testid="projects-section">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <SectionHeading overline={p.overline} title={p.title} />

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
          {p.list.map((proj, i) => (
            <Reveal key={i} delay={i * 0.06} className={i === 0 ? "md:col-span-2" : ""}>
              <TiltCard
                max={5}
                onClick={() => setOpen(i)}
                data-cursor="hover"
                data-testid={`project-card-${i}`}
                className="group relative h-full cursor-pointer overflow-hidden rounded-3xl border border-white/10 bg-ink-surface p-8 md:p-10 transition-colors duration-500 hover:border-flame/30"
              >
                <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-flame/0 blur-3xl transition-all duration-500 group-hover:bg-flame/10" />
                <div className="relative flex h-full flex-col">
                  <div className="flex items-start justify-between">
                    <div className="flex flex-wrap gap-2">
                      {proj.tags.map((tag) => (
                        <span key={tag} className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-mono uppercase tracking-wide text-white/50">{tag}</span>
                      ))}
                    </div>
                    <span className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 transition-all duration-300 group-hover:bg-flame group-hover:text-black group-hover:rotate-45">
                      <ArrowUpRight size={18} />
                    </span>
                  </div>
                  <h3 className={`mt-6 font-heading font-bold ${i === 0 ? "text-4xl md:text-5xl" : "text-2xl md:text-3xl"}`}>{proj.name}</h3>
                  <p className="mt-3 text-white/55 max-w-xl">{proj.tagline}</p>
                  <div className="mt-auto pt-8 flex items-center gap-2 text-sm font-semibold text-flame">
                    <LayoutDashboard size={16} /> {p.open}
                  </div>
                </div>
              </TiltCard>
            </Reveal>
          ))}
        </div>
      </div>

      {/* modal */}
      <AnimatePresence>
        {open !== null && (
          <motion.div
            className="fixed inset-0 z-[120] flex items-center justify-center p-4 md:p-8"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="project-modal"
          >
            <motion.div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={() => setOpen(null)} />
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 16 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative z-10 w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-3xl glass"
            >
              <button onClick={() => setOpen(null)} data-cursor="hover" data-testid="modal-close"
                className="absolute right-5 top-5 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-black/40 hover:bg-flame hover:text-black transition-colors">
                <X size={18} />
              </button>

              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="p-8 md:p-10">
                  <div className="flex flex-wrap gap-2">
                    {p.list[open].tags.map((tag) => (
                      <span key={tag} className="rounded-full border border-flame/30 bg-flame/10 px-3 py-1 text-[11px] font-mono uppercase tracking-wide text-flame">{tag}</span>
                    ))}
                  </div>
                  <h3 className="mt-5 font-heading text-3xl md:text-4xl font-bold">{p.list[open].name}</h3>
                  <p className="mt-2 text-white/55">{p.list[open].tagline}</p>

                  <div className="mt-7">
                    <h4 className="text-xs font-mono uppercase tracking-[0.2em] text-flame">{p.challenge}</h4>
                    <p className="mt-2 text-white/70 leading-relaxed">{p.list[open].challenge}</p>
                  </div>
                  <div className="mt-6">
                    <h4 className="text-xs font-mono uppercase tracking-[0.2em] text-flame">{p.solution}</h4>
                    <p className="mt-2 text-white/70 leading-relaxed">{p.list[open].solution}</p>
                  </div>
                  <div className="mt-5 grid grid-cols-2 gap-2">
                    {p.list[open].features.map((f) => (
                      <div key={f} className="flex items-center gap-2 text-sm text-white/65">
                        <Check size={14} className="text-flame shrink-0" /> {f}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="p-8 md:p-10 bg-white/[0.02] flex flex-col">
                  <div className="h-[300px] md:h-[340px]"><MockUI project={p.list[open]} /></div>
                  <div className="mt-6">
                    <h4 className="text-xs font-mono uppercase tracking-[0.2em] text-flame">{p.impact}</h4>
                    <div className="mt-3 grid grid-cols-3 gap-3">
                      {p.list[open].impact.map((m, i) => (
                        <div key={i} className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-center">
                          <div className="font-heading text-2xl font-bold text-flame">{m.v}</div>
                          <div className="mt-1 text-[10px] text-white/45">{m.l}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
